// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegion,
  $Model,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Row,
} from 'reactstrap';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getLatestRegionSeasonByRegionId,
} from 'common.helpers/dist/browser';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $SetActiveRegionIdAction,
  $SetActiveRegionSeasonIdAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  setActiveRegionId: $SetActiveRegionIdAction;
  setActiveRegionSeasonId: $SetActiveRegionSeasonIdAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  RegionSeasons: Array<$Model<'RegionSeason'>>;
};

class InputRegionSeasonView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      RegionSeasons: [],
    };
  }

  componentDidMount(): void {
    this.getData();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;
    const {
      activeRegionId,
    } = main;

    if (activeRegionId !== prevProps.main.activeRegionId) {
      this.getData();
    }
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;
    const {
      activeRegionId,
    } = main;

    try {
      if (activeRegionId !== null) {
        await this.getRegionSeasonsData({
          RegionId: activeRegionId,
        });
      }
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  async getRegionSeasonsData({
    RegionId,
  }: {
    RegionId: string;
  }): Promise<void> {
    const {
      main,
    } = this.props;
    const {
      regions,
    } = main;

    const region = _.find(
      regions,
      {
        id: RegionId,
      },
    ) as $FullRegion | undefined;

    if (region) {
      const {
        RegionSeasons,
      } = region;

      await this.setStateAsync({
        RegionSeasons,
      });
    }
  }

  async setRegionId({
    RegionId,
  }: {
    RegionId: string;
  }): Promise<void> {
    const {
      main,
      setActiveRegionId,
    } = this.props;
    const {
      regions,
    } = main;

    try {
      await setActiveRegionId({
        RegionId,
      });

      const latestRegionSeason: $Model<'RegionSeason'> | null = getLatestRegionSeasonByRegionId({
        RegionId,
        regions,
      });

      const RegionSeasonId: null | string = latestRegionSeason !== null
        ? latestRegionSeason.id
        : null;

      if (RegionSeasonId !== null) {
        await this.setRegionSeasonId({
          RegionSeasonId,
        });
      }
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  async setRegionSeasonId({
    RegionSeasonId,
  }: {
    RegionSeasonId: string;
  }): Promise<void> {
    const {
      setActiveRegionSeasonId,
    } = this.props;

    try {
      await setActiveRegionSeasonId({
        RegionSeasonId,
        resetRegatta: true,
      });
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  render() {
    const {
      main,
    } = this.props;
    const {
      RegionSeasons,
    } = this.state;

    return (
      <Row className={'no-gutters'}>
        <Col xs={8}>
          <InputGroup>
            <InputGroupText>
              <em className={'icon-globe my-1'} />
            </InputGroupText>
            <Input
              name={'RegionId'}
              onChange={(e) => this.setRegionId({
                RegionId: e.target.value,
              })}
              type={'select'}
              value={main.activeRegionId || undefined}
            >
              {main.activeRegionId === null && (
                <option />
              )}
              {main.regions.map((item: $FullRegion) => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.Name}
                </option>
              ))}
            </Input>
          </InputGroup>
        </Col>
        <Col xs={4}>
          <Input
            name={'RegionSeasonId'}
            onChange={(e) => this.setRegionSeasonId({
              RegionSeasonId: e.target.value,
            })}
            type={'select'}
            value={main.activeRegionSeasonId || undefined}
          >
            {main.activeRegionSeasonId === null && (
              <option />
            )}
            {RegionSeasons.map((item: $Model<'RegionSeason'>) => (
              <option
                key={item.id}
                value={item.id}
              >
                {item.Name}
              </option>
            ))}
          </Input>
        </Col>
      </Row>
    );
  }
}

export default InputRegionSeasonView;
